/**
 * FaceClipPlayer — loads and plays pre-converted face animation clips.
 *
 * Each clip is a JSON file with per-frame ARKit blendshape weights
 * (produced by aosTools/threejs/convert_face_fbx_to_arkit.py).
 *
 * The face graph evaluator produces weights like { listening_neutral_face: 0.8 }.
 * This player returns the blended 52-element ARKit weight array for the
 * current time, which HeadModel applies alongside LiveLink data.
 */

import { playBodyGesture } from './GestureChannel'
import { devLog } from './devLog'
import { resolveActiveCharacter } from './characterState'
import { listAnimationAssets, listGestures } from './assetManagerApi.js'
import { clipRuntimeKeys } from './clipRuntimeKeys.js'
import { fetchWithRetry } from './fetchRetry.js'

// Stock face clips + gesture mappings now live in the DB+S3, served by
// the per-character animation-assets + gestures endpoints. The legacy
// public/assets/animations and public/assets/gestures.json fetches are gone.

// Defaults for gesture playback when a mapping doesn't override them.
const DEFAULT_GESTURE_DURATION_MS = 3000
const DEFAULT_FACE_WEIGHT         = 0.8

class FaceClipPlayer {
  constructor() {
    this._clips = {}     // { name: { fps, frames: [{ timeCode, blendshapeWeights }] } }
    this._active = {}    // { name: { startTime, lastFrameIdx } }
    this._overrides = {} // { name: { weight, expiresAt } } — manual debug plays
    this._gestures = {}  // { gestureName: { face: clipName } }
    this._loaded = false
    // Reusable output buffer — zero-filled each frame instead of allocating a new array
    this._resultBuffer = new Float32Array(52)
    // Reusable scratch object for getBlendedWeights — mutated in place each
    // frame to merge faceWeights + _overrides without allocating a new {}.
    this._effective = {}
  }

  /**
   * Play a named gesture by server-sent tag (e.g. "smile", "leans in").
   * Looks up gestures.json, resolves to a clip, triggers a manualPlay.
   * Case-insensitive; substring-matches so multi-word server tags still
   * resolve to short keys (e.g. "leans in, grin spreads" → "lean in").
   *
   * @param {string} gestureName
   * @param {object} [opts] { body } — pass `{ body: false }` to play the face
   *   half only. Used when a kimodo-GENERATED body clip is driving the gesture:
   *   GestureChannel is a single slot, so letting the mapping's stock `body`
   *   clip play too would clobber the generated one (last play wins).
   */
  playGesture(gestureName, { body = true } = {}) {
    if (!gestureName) return
    const needle = String(gestureName).toLowerCase().trim()
    // Exact match first, then substring
    let entry = this._gestures[needle]
    if (!entry) {
      const key = Object.keys(this._gestures).find(k => k !== '*' && needle.includes(k))
      if (key) entry = this._gestures[key]
    }
    if (!entry) entry = this._gestures['*']
    if (!entry) {
      devLog(`[FaceClipPlayer] No gesture mapping for "${gestureName}" (and no "*" fallback)`)
      return
    }

    const durationMs = entry.duration ?? DEFAULT_GESTURE_DURATION_MS
    const parts = []

    // Face clip (if the mapping has one) — loaded via FaceClipPlayer's manualPlay.
    if (entry.face) {
      this.manualPlay(entry.face, DEFAULT_FACE_WEIGHT, durationMs)
      parts.push(`face=${entry.face}`)
    }

    // Body clip (if the mapping has one) — layered via GestureChannel on top
    // of the SM-driven base animation. The body action must already exist in
    // window.__AVATAR_ACTIONS__ (i.e. referenced from manifest.json body[]).
    if (entry.body && body) {
      playBodyGesture(entry.body, {
        durationMs,
        peakWeight: entry.bodyWeight ?? undefined,
      })
      parts.push(`body=${entry.body}`)
    }

    if (parts.length === 0) {
      devLog(`[FaceClipPlayer] Gesture "${gestureName}" has no face or body mapping`)
    } else {
      devLog(`[FaceClipPlayer] gesture "${gestureName}" -> ${parts.join(', ')} (${durationMs}ms)`)
    }
  }

  /**
   * Manually play a clip for `durationMs` at the given weight, ignoring the
   * face graph. Used by the PlayFaceClip debug blueprint node to test clips
   * without needing to drive the state machine.
   */
  manualPlay(name, weight = 1.0, durationMs = 5000) {
    if (!this._clips[name]) {
      console.warn(`[FaceClipPlayer] manualPlay: clip not loaded: ${name}`)
      return
    }
    this._overrides[name] = { weight, expiresAt: performance.now() + durationMs }
    this.play(name)
  }

  /** Load all face clips from the manifest + gestures.json. Call once on startup.
   *
   * Failures throw — never silently fall back to an empty clip set or empty
   * gesture map. The App-level error boundary renders a visible overlay so
   * the user knows the backend / asset pipeline is broken. See
   * docs/UX_PRINCIPLES.md.
   *
   * Per-clip JSON fetch failures are still collected and warned (one bad
   * clip shouldn't take down the whole avatar), but the manifest and
   * gestures.json themselves are non-negotiable.
   */
  async loadAll() {
    const charConfig = await resolveActiveCharacter()
    const slug = charConfig?.id
    if (!slug) throw new Error('FaceClipPlayer.loadAll: no character slug')

    // Pull face clips from the DB+S3 — same animation-assets endpoint
    // AvatarModel uses for body clips, just filtered to kind=face.
    const all = await listAnimationAssets(slug)
    const faceAssets = (all ?? []).filter(a => a.kind === 'face')

    const failed = []
    const clipsP = Promise.all(faceAssets.map(async (asset) => {
      const { name, file_url: url } = asset
      try {
        // Retry transient network blips (net::ERR_NETWORK_CHANGED under CI
        // load) so one dropped request doesn't silently drop a face clip.
        const res = await fetchWithRetry(url)
        if (!res.ok) { console.warn(`[FaceClipPlayer] Failed to load ${url}: ${res.status}`); failed.push(name); return }
        const data = await res.json()
        this._clips[name] = data
        // Every runtime key this asset resolves by, registered by its OWNER as
        // the clip lands. The `custom_face_<id>` alias is the same JSON under a
        // second name (clipRuntimeKeys.js), so CustomAssetManager used to
        // re-download the file to produce it. Doing it here costs nothing and
        // means the alias is never missing for a clip that is present.
        for (const key of clipRuntimeKeys(asset)) this.aliasClip(key, name)
      } catch (e) {
        console.warn(`[FaceClipPlayer] Error loading ${url}:`, e)
        failed.push(name)
      }
    }))

    // Gesture mappings come from the gestures table: each row references
    // a body and/or face animation_asset by ID; we resolve those back to
    // clip names (which is what _gestures consumers expect at runtime).
    const gesturesP = (async () => {
      const rows = await listGestures(slug)
      // animation-assets contract: each row has { id, name, kind }. Build
      // an id→name map once so each gesture row resolves cheaply.
      const byId = new Map((all ?? []).map(a => [a.id, a.name]))
      for (const g of rows ?? []) {
        // AvatarModel registers every animation_asset in __AVATAR_ACTIONS__
        // under its asset.name (the value we set on the manifest at upload
        // time). The gesture's body/face fields just need that name —
        // playBodyGesture and manualPlay both look it up by name.
        // The gestures endpoint returns the asset references in camelCase
        // (bodyAnimationId / faceAnimationId) after the asset-manager
        // migration; older payloads used snake_case. Accept either so a
        // field-name change on the server can't silently null out every
        // gesture mapping (which is exactly what broke the gesture/prefab
        // body+face channels — ENG-598).
        const bodyId = g.bodyAnimationId ?? g.body_animation_id ?? null
        const faceId = g.faceAnimationId ?? g.face_animation_id ?? null
        const bodyName = bodyId ? byId.get(bodyId) : null
        const faceName = faceId ? byId.get(faceId) : null
        this._gestures[(g.tag ?? '').toLowerCase()] = {
          face: faceName,
          body: bodyName,
          duration: g.duration_ms ?? 3000,
          bodyWeight: g.body_weight ?? 0.7,
        }
      }
      console.log(`[FaceClipPlayer] Gesture map: ${Object.keys(this._gestures).join(', ')}`)
    })()

    await Promise.all([clipsP, gesturesP])
    this._loaded = true
    const loaded = Object.keys(this._clips)
    console.log(`[FaceClipPlayer] ${loaded.length} clips ready: ${loaded.join(', ')}` + (failed.length ? ` | FAILED: ${failed.join(', ')}` : ''))
  }

  /** True if a clip is loaded under `name`. Lets callers resolve the actual
   * runtime key (seeded clips register under their name; custom uploads also
   * under custom_face_<id>) before calling manualPlay. */
  hasClip(name) {
    return Object.prototype.hasOwnProperty.call(this._clips, name)
  }

  /** Register a face clip at runtime (e.g. from a custom upload). */
  loadCustomClip(name, clipData) {
    this._clips[name] = clipData
  }

  /**
   * Point a SECOND key at an already-loaded clip — no re-fetch, no re-parse.
   *
   * Every animation asset also resolves by a `custom_face_<assetId>` alias
   * (clipRuntimeKeys.js), which names the same JSON as the asset's own name.
   * Producing it by re-downloading the file was waste; this is the free path.
   *
   * The frame data is SHARED, not copied, unlike the body-clip alias
   * (__ALIAS_BODY_CLIP__ has to clone because three.js caches an action per clip
   * object). Face clips are read-only per-frame weight arrays with no per-clip
   * playback state on them — `_active[name]` holds that, keyed separately — so
   * two keys over one array is correct here and costs nothing.
   *
   * @returns {boolean} whether `alias` now resolves.
   */
  aliasClip(alias, sourceName) {
    if (!alias || alias === sourceName) return false
    if (this.hasClip(alias)) return true          // idempotent
    if (!this.hasClip(sourceName)) return false   // source not loaded (yet)
    this._clips[alias] = this._clips[sourceName]
    return true
  }

  /** Register a gesture tag → clip/body mapping at runtime. */
  registerGestureMapping(tag, mapping) {
    this._gestures[tag.toLowerCase().trim()] = mapping
  }

  /**
   * Start or update a face clip's playback.
   * Called every frame by HeadModel based on face graph weights.
   */
  play(name) {
    if (!this._clips[name]) return
    if (!this._active[name]) {
      this._active[name] = { startTime: performance.now(), lastFrameIdx: 0 }
    }
  }

  /** Stop a face clip. */
  stop(name) {
    delete this._active[name]
  }

  /** Cancel all manual overrides immediately (called by stopSpeech). */
  cancelAllOverrides() {
    for (const k in this._overrides) delete this._overrides[k]
  }

  /**
   * Write per-clip timings ({time,duration,remaining,ratio}) for every
   * active face clip directly into the supplied target object — typically
   * `debugState.clipTimings`. Reuses existing entry objects in place to
   * avoid the per-frame object alloc the old `return new {}` shape forced.
   * Stale entries (clips no longer face-driven) are dropped here so callers
   * don't see ghost timings.
   */
  writeTimingsInto(target) {
    if (!target) return
    const now = performance.now()
    // First, remove face-clip entries that are no longer playing. Body
    // clip timings are owned by AvatarModel and live alongside; only
    // touch keys that exist on the face player.
    for (const k in target) {
      if (this._clips[k] && !this._active[k]) delete target[k]
    }
    for (const name in this._active) {
      const active = this._active[name]
      const clip = this._clips[name]
      if (!clip?.frames?.length) continue
      const duration = clip.frames[clip.frames.length - 1].timeCode
      if (duration <= 0) continue
      const elapsed   = (now - active.startTime) / 1000
      const time      = elapsed % duration
      const existing  = target[name]
      if (existing) {
        existing.time      = time
        existing.duration  = duration
        existing.remaining = duration - time
        existing.ratio     = time / duration
      } else {
        target[name] = { time, duration, remaining: duration - time, ratio: time / duration }
      }
    }
  }

  /**
   * Get blended 52-element ARKit weights for all active clips.
   * @param {Object} faceWeights - { clipName: weight } from the face graph evaluator
   * @returns {Float32Array|null} Blended 52-element array, or null if nothing active
   */
  getBlendedWeights(faceWeights) {
    if (!this._loaded) return null

    // Hot path — called every frame from HeadModel. We avoid:
    //   • { ...faceWeights } spread       (was a fresh object every frame)
    //   • Object.entries(...)             (fresh array of pairs every frame)
    //   • Object.keys(this._active)       (fresh array of strings every frame)
    // by walking faceWeights and _overrides as for-in loops directly into a
    // single reusable scratch object kept on the instance.
    const now    = performance.now()
    const result = this._resultBuffer
    result.fill(0)

    const eff = this._effective
    // Reset scratch — drop stale keys, then write current frame's data.
    for (const k in eff) delete eff[k]
    if (faceWeights) {
      for (const k in faceWeights) {
        if (k !== '__livelink__') eff[k] = faceWeights[k]
      }
    }
    // Merge in manual debug overrides (PlayFaceClip etc.). Expired ones are
    // pruned in the same pass — saves a separate cleanup walk.
    for (const name in this._overrides) {
      const o = this._overrides[name]
      if (o.expiresAt < now) {
        delete this._overrides[name]
        continue
      }
      const cur = eff[name] ?? 0
      if (o.weight > cur) eff[name] = o.weight
    }

    let hasAny = false

    for (const name in eff) {
      const weight = eff[name]
      if (weight <= 0) continue
      const clip = this._clips[name]
      if (!clip || !clip.frames || clip.frames.length === 0) continue

      // Ensure playback is active
      this.play(name)
      const active = this._active[name]
      if (!active) continue

      // Seek to current frame based on elapsed time (looping)
      const elapsed = (now - active.startTime) / 1000
      const frames = clip.frames
      const duration = frames[frames.length - 1].timeCode
      const t = duration > 0 ? elapsed % duration : 0

      // Walk forward from last frame; reset to 0 when time wraps past duration
      let frameIdx = active.lastFrameIdx ?? 0
      if (frameIdx >= frames.length || frames[frameIdx].timeCode > t) frameIdx = 0
      while (frameIdx < frames.length - 1 && frames[frameIdx + 1].timeCode <= t) {
        frameIdx++
      }
      active.lastFrameIdx = frameIdx

      // Interpolate between current and next frame for smooth transitions
      // (especially across loop boundaries where values can jump).
      const weightsA = frames[frameIdx].blendshapeWeights
      if (!weightsA) continue
      const nextIdx = (frameIdx + 1) % frames.length
      const weightsB = frames[nextIdx].blendshapeWeights
      const tA = frames[frameIdx].timeCode
      const tB = nextIdx > frameIdx ? frames[nextIdx].timeCode : duration
      const frac = tB > tA ? (t - tA) / (tB - tA) : 0
      const limit = weightsA.length < 52 ? weightsA.length : 52
      if (weightsB && frac > 0 && frac < 1) {
        for (let i = 0; i < limit; i++) {
          result[i] += (weightsA[i] + (weightsB[i] - weightsA[i]) * frac) * weight
        }
      } else {
        for (let i = 0; i < limit; i++) {
          result[i] += weightsA[i] * weight
        }
      }
      hasAny = true
    }

    // Stop clips no longer being driven. for-in over the live _active object
    // avoids the Object.keys allocation; safe to delete during iteration
    // because for-in materializes the key list lazily.
    for (const name in this._active) {
      const w = eff[name]
      if (!w || w <= 0) this.stop(name)
    }

    return hasAny ? result : null
  }
}

export const faceClipPlayer = new FaceClipPlayer()

// Expose for e2e tests and debugging
if (typeof window !== 'undefined') {
  window.__FACE_CLIP_PLAYER__ = faceClipPlayer
}
